import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axiosClient, { linkImage } from "../../../axios-client";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { useColorModeValue } from "@chakra-ui/react";


import countriesData from "../../../assets/dataJson/countries.json";
import { useStateContext } from "../../../context/ContextProvider";

const currencies = Array.from(
  new Map(
    countriesData.countries
      .filter(c => c.currency && c.currency.code)
      .map(c => [c.currency.code, { ...c.currency, flag: c.label.split(' ')[0] }])
  ).values()
).sort((a, b) => a.code.localeCompare(b.code));

const CURRENCY_CHOICES = currencies.map(c => ({
  value: c.code,
  label: `${c.flag} ${c.name} (${c.code})`
}));



const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function EditBusiness() {
    const { t } = useTranslation();
  const { setCurrentBusiness } = useStateContext();
  const navigate = useNavigate();
  const location = useLocation();
  const biz = location.state?.business;

  const [form, setForm] = useState({
    name: biz?.name || "",
    description: biz?.description || "",
    currency: biz?.currency || "MAD",
    order_currency: biz?.order_currency || "MAD",
    exchange_rate_type: biz?.exchange_rate_type || "Auto",
    custom_exchange_rate: biz?.custom_exchange_rate || "",
  });

  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(
    biz?.logo ? (biz?.logo ? (biz.logo.startsWith('http') ? biz.logo : `${linkImage}${biz.logo}`) : undefined) : null
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Dark mode colors
  const bg = useColorModeValue("#f8fafc", "#1a202c");
  const cardBg = useColorModeValue("#ffffff", "#2d3748");
  const textColor = useColorModeValue("#1e293b", "#f7fafc");
  const secondaryText = useColorModeValue("#64748b", "#94a3b8");
  const borderColor = useColorModeValue("#e2e8f0", "#4a5568");
  const inputBg = useColorModeValue("#ffffff", "#1a202c");
  const labelColor = useColorModeValue("#374151", "#cbd5e1");
  const backgroundSelect = useColorModeValue("#f1f5f9", "#4a5568")
  const inputError = useColorModeValue("#fef2f2", "#4a2b2b");


  const selectStyles = {
    control: (base, state) => ({
      ...base,
      padding: "2px",
      borderRadius: 10,
      borderColor: state.isFocused ? "#7B5EA7" : borderColor,
      boxShadow: state.isFocused ? "0 0 0 1px #7B5EA7" : "none",
      "&:hover": { borderColor: "#7B5EA7" },
      fontSize: 14,
      background: inputBg,
      color: textColor,
    }),
    singleValue: (base) => ({
      ...base,
      color: textColor,
    }),
    menu: (base) => ({
      ...base,
      background: cardBg,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      backgroundColor: state.isSelected ? "#7B5EA7" : state.isFocused ? (backgroundSelect) : cardBg,
      color: state.isSelected ? "#fff" : textColor,
      "&:active": { backgroundColor: "#7B5EA7", color: "#fff" },
    }),
  };

  useEffect(() => {
    if (!biz) navigate("/mes-businesses");
  }, [biz, navigate]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Le nom du business est requis.";
    if (form.name.trim().length < 2) errs.name = "Le nom doit contenir au moins 2 caractères.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("description", form.description.trim());
    fd.append("currency", form.currency);
    fd.append("order_currency", form.order_currency);
    fd.append("exchange_rate_type", form.exchange_rate_type);
    if (form.exchange_rate_type === "Manual") {
      fd.append("custom_exchange_rate", form.custom_exchange_rate);
    }

    if (logo) fd.append("logo", logo);

    axiosClient
      .patch(`/businesses/${biz.id}/`, fd)
      .then((response) => {
        showToast(t("Business mis à jour avec succès !"));
        const data = response.data;
        setCurrentBusiness(data);
        setTimeout(() => navigate("/mes-businesses"), 1200);
      })
      .catch((err) => {
        const detail = err.response?.data?.detail || err.response?.data?.message || "Erreur lors de la mise à jour.";
        showToast(detail, "error");
        setLoading(false);
      });
  };

  if (!biz) return null;

  return (
    <div style={{ padding: "32px", minHeight: "100vh", background: bg }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 24, zIndex: 9999,
          background: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
          color: toast.type === "error" ? "#dc2626" : "#166534",
          borderRadius: 10, padding: "12px 20px", boxShadow: "0 8px 30px rgba(0,0,0,.12)",
          fontWeight: 500, fontSize: 14,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
        <button onClick={() => navigate("/mes-businesses")} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
          border: `1px solid ${borderColor}`, borderRadius: 9, background: cardBg,
          color: secondaryText, cursor: "pointer", fontWeight: 500, fontSize: 14,
        }}>
          <ArrowLeftIcon />{t("Retour")}</button>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: textColor }}>{t("✏️ Modifier le Business")}</h1>
          <p style={{ margin: "4px 0 0", color: secondaryText, fontSize: 13 }}>{biz.name}</p>
        </div>
      </div>

      {/* Card */}
      <div style={{
        maxWidth: 600, background: cardBg, borderRadius: 18, padding: "32px 36px",
        boxShadow: "0 4px 24px rgba(0,0,0,.08)", border: `1px solid ${borderColor}`,
      }}>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 22 }}>

          {/* Logo */}
          <div style={{ textAlign: "center" }}>
            <label htmlFor="logo-upload" style={{ cursor: "pointer" }}>
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" style={{
                  width: 90, height: 90, borderRadius: 18, objectFit: "cover",
                  border: `2px solid ${borderColor}`,
                }} />
              ) : (
                <div style={{
                  width: 90, height: 90, borderRadius: 18, margin: "0 auto",
                  background: "linear-gradient(135deg, #7B5EA7, #9F7AEA)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 32, fontWeight: 700,
                }}>
                  {form.name.charAt(0).toUpperCase() || "🏢"}
                </div>
              )}
              <p style={{ fontSize: 12, color: secondaryText, marginTop: 8 }}>{t("Cliquez pour changer le logo")}</p>
            </label>
            <input id="logo-upload" type="file" accept="image/*" onChange={handleLogo} style={{ display: "none" }} />
          </div>

          {/* Name */}
          <Field label={t("Nom du Business *")} error={errors.name} labelColor={labelColor}>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t("Nom du business")}
              style={inputStyle(!!errors.name, borderColor, inputBg, textColor)}
            />
          </Field>

          {/* Description */}
          <Field label={t("Description")} labelColor={labelColor}>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder={t("Description...")}
              style={{ ...inputStyle(false, borderColor, inputBg, textColor), resize: "vertical", minHeight: 90 }}
            />
          </Field>

          {/* Currency */}
          <Field label={t("Devise (Monnaie)")} labelColor={labelColor}>
            <Select
              options={CURRENCY_CHOICES}
              value={CURRENCY_CHOICES.find(c => c.value === form.currency)}
              onChange={(opt) => setForm(prev => ({ ...prev, currency: opt.value }))}
              placeholder={t("Sélectionnez une devise...")}
              styles={selectStyles}
              isSearchable={true}
            />
          </Field>

          {/* Order Currency */}
          <Field label={t("Devise des Commandes (Order Currency)")} labelColor={labelColor}>
            <Select
              options={CURRENCY_CHOICES}
              value={CURRENCY_CHOICES.find(c => c.value === form.order_currency)}
              onChange={(opt) => setForm(prev => ({ ...prev, order_currency: opt.value }))}
              placeholder={t("Sélectionnez une devise pour les commandes...")}
              styles={selectStyles}
              isSearchable={true}
            />
          </Field>

          {/* Taux de Change */}
          <Field label={t("Taux de Change")} labelColor={labelColor}>
            <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", color: textColor }}>
                <input
                  type="radio"
                  name="exchange_rate_type"
                  value="Auto"
                  checked={form.exchange_rate_type === "Auto"}
                  onChange={handleChange}
                  style={{ width: 16, height: 16 }}
                />{t("Automatique")}</label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", color: textColor }}>
                <input
                  type="radio"
                  name="exchange_rate_type"
                  value="Manual"
                  checked={form.exchange_rate_type === "Manual"}
                  onChange={handleChange}
                  style={{ width: 16, height: 16 }}
                />{t("Manuel")}</label>
            </div>
            {form.exchange_rate_type === "Manual" && (
              <div style={{ marginTop: 12 }}>
                <input
                  type="number"
                  name="custom_exchange_rate"
                  value={form.custom_exchange_rate}
                  onChange={handleChange}
                  placeholder={t("Entrez le taux de change (ex: 10.5)")}
                  step="0.0001"
                  style={inputStyle(!!errors.custom_exchange_rate, borderColor, inputBg, textColor)}
                />
                <p style={{ fontSize: 12, color: secondaryText, marginTop: 4 }}>
                  1 {form.order_currency} = X {form.currency}
                </p>
              </div>
            )}
            {form.exchange_rate_type === "Auto" && (
              <p style={{ fontSize: 12, color: secondaryText, marginTop: 8 }}>
                Le taux sera récupéré automatiquement via API (1 {form.order_currency} → {form.currency})
              </p>
            )}
          </Field>



          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: "13px", background: loading ? "#e2e8f0" : "linear-gradient(135deg, #7B5EA7, #9F7AEA)",
              color: loading ? "#94a3b8" : "#fff", border: "none", borderRadius: 12,
              fontWeight: 700, fontSize: 15, cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 4px 14px rgba(123,94,167,.35)", transition: "all .2s",
            }}>
            {loading ? "Mise à jour..." : "✓ Enregistrer les modifications"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, error, children, labelColor }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: labelColor }}>{label}</label>
      {children}
      {error && <span style={{ fontSize: 12, color: "#dc2626" }}>{error}</span>}
    </div>
  );
}

function inputStyle(hasError, borderColor, inputBg, textColor, inputError) {
  return {
    padding: "11px 14px",
    border: `1px solid ${hasError ? "#fca5a5" : borderColor}`,
    borderRadius: 10, fontSize: 14, outline: "none", width: "100%",
    background: hasError ? (inputError) : inputBg,
    color: textColor, boxSizing: "border-box",
  };
}

